import { FormControlProps } from '../../interfaces/FormControlProps';
import { IFormConfig } from '../../interfaces/FormConfig';
import { FormConfigService } from '../../services/FormConfigService';
import { getResourceString } from '../../kentico/resource-strings';

export class FormConfigHelpers {

  /**
   * Initializes a default form configuration
   * @param formName The name of the form
   * @param formTitle An optional title of the form
   * @returns A default instance of FormConfig
   */
  static getDefaultFormConfig(formName: string, formTitle?: string): IFormConfig {
    return {
      fillViaApi: `/internal/formandcontentreact/get/${formName}`,
      startForm: 'form',
      formSteps: ['form'],
      formTitle: '',
      postUrl: `/internal/formandcontentreact/set/${formName}`,
      doFormMultipartPost: true,
      ignoreVisibilityConditionDuringSubmit: true,
      thanksPageUrl: '',
      errorPageUrl: '',
      OnSubmitSuccessLinkIncludeUrlGuid: true,
      reactRouteOnSuccess: '',
      reactRouteOnFailure: '-',

      thanksPageUrlFields: [],
      pageSettings: {
        form: {
          title: formTitle || 'Formulier',
          subtitle: ''
        }
      }
    } as IFormConfig;
  }

  static hasOutputMappings(component: React.Component<FormControlProps>) {
      const formConfig = component.props.formConfig;
      const formFieldDefinition = component.props.formFieldDefinition;

      // Do we have it?
      return (
          formConfig.outputMappings  &&
            formConfig.outputMappings[formFieldDefinition.triggerDatasource || formFieldDefinition.datasource] 
      );
  }

  static outputMappings(component: React.Component<FormControlProps>, doPerItem= (item : any) => item) {
      if (!this.hasOutputMappings(component)) {
          return [];
      }

      const formConfig = component.props.formConfig;
      const formFieldDefinition = component.props.formFieldDefinition;

      // Return mappings
      const mappings = formConfig.outputMappings[formFieldDefinition.triggerDatasource || formFieldDefinition.datasource];
      return mappings.map(doPerItem);
  }

  static hasFormSteps(formConfig: IFormConfig): boolean {
      const formSteps = formConfig.formSteps;
    
      // Do we have it?
      return (
          formSteps &&
          formSteps.length > 0
      );
  }

  static formStepsForEach(formConfig: IFormConfig, doPerStep: (stepName: string) => void) {
      if (!this.hasFormSteps(formConfig)) {
          return;
      }

      formConfig.formSteps.forEach(doPerStep);
  }

    static getMergedFormConfig(formConfig : IFormConfig): IFormConfig {
        const globalFormConfig = FormConfigService.getFormConfig();
        
        if (formConfig.formOverrides === undefined) {
            (formConfig as any).formOverrides = {};
        }

        if (formConfig.requiredFieldTip === undefined) { // empty string is allowed, so do check on undefined
            if (globalFormConfig.requiredFieldTip === "") {
                formConfig.requiredFieldTip = "";
            } else {
                formConfig.requiredFieldTip = getResourceString("Form.RequiredFieldsHint");
            }
        } else {
            if (formConfig.requiredFieldTip === "") {
                formConfig.requiredFieldTip = "";
            } else {
                formConfig.requiredFieldTip = getResourceString("Form.RequiredFieldsHint");
            }

        }

        if (formConfig.autoInsertFormFieldGroup === undefined && 
            globalFormConfig.autoInsertFormFieldGroup !== undefined) {
            formConfig.autoInsertFormFieldGroup = globalFormConfig.autoInsertFormFieldGroup;
        }

        for (const formOverride in globalFormConfig.formOverrides) {
            if (globalFormConfig.formOverrides.hasOwnProperty(formOverride)) {
                if (formConfig.formOverrides[formOverride] !== undefined) {
                    formConfig.formOverrides[formOverride] = (Object as any).assign({},                  
                        globalFormConfig.formOverrides[formOverride],
                        formConfig.formOverrides[formOverride]);    
                } else {
                    formConfig.formOverrides[formOverride] = globalFormConfig.formOverrides[formOverride];
                }

            }
        }
        
        if (formConfig.urlMappings === undefined) {
            formConfig.urlMappings = {};
        }

        for (const urlMapping in globalFormConfig.urlMappings) {
            if (globalFormConfig.urlMappings.hasOwnProperty(urlMapping)) {
                if (formConfig.urlMappings[urlMapping] === undefined) {
                    formConfig.urlMappings[urlMapping] = globalFormConfig.urlMappings[urlMapping];
                }
            }
        }

        if (formConfig.urlMappingsRelatedData === undefined) {
            formConfig.urlMappingsRelatedData = {};
        }

        for (const urlMappingRelatedData in globalFormConfig.urlMappingsRelatedData) {
            if (globalFormConfig.urlMappingsRelatedData.hasOwnProperty(urlMappingRelatedData)) {
                if (formConfig.urlMappingsRelatedData[urlMappingRelatedData] === undefined) {
                    formConfig.urlMappingsRelatedData[urlMappingRelatedData] = 
                        globalFormConfig.urlMappingsRelatedData[urlMappingRelatedData];
                }
            }
        }

        if (formConfig.parameterMappings === undefined) {
            formConfig.parameterMappings = {};
        }

        for (const parameterMapping in globalFormConfig.parameterMappings) {
            if (globalFormConfig.parameterMappings.hasOwnProperty(parameterMapping)) {
                if (formConfig.parameterMappings[parameterMapping] !== undefined) {
                    formConfig.parameterMappings[parameterMapping] = (Object as any).assign({},                     
                        globalFormConfig.parameterMappings[parameterMapping],
                        formConfig.parameterMappings[parameterMapping]);    
                } else {
                    formConfig.parameterMappings[parameterMapping] = 
                        globalFormConfig.parameterMappings[parameterMapping];
                }
            }
        }

        if (formConfig.outputMappings === undefined) {
            formConfig.outputMappings = {};
        }

        for (const outputMapping in globalFormConfig.outputMappings) {
            if (globalFormConfig.outputMappings.hasOwnProperty(outputMapping)) {
                if (formConfig.outputMappings[outputMapping] !== undefined) {
                    formConfig.outputMappings[outputMapping] = (Object as any).assign({},                     
                        globalFormConfig.outputMappings[outputMapping],
                        formConfig.outputMappings[outputMapping]);    
                } else {
                    formConfig.outputMappings[outputMapping] = 
                        globalFormConfig.outputMappings[outputMapping];
                }
            }
        }

        if (formConfig.datasourceConditions === undefined) {
            formConfig.datasourceConditions = {};
        }

        for (const datasourceCondition in globalFormConfig.datasourceConditions) {
            if (globalFormConfig.datasourceConditions.hasOwnProperty(datasourceCondition)) {
                if (formConfig.datasourceConditions[datasourceCondition] !== undefined) {
                    formConfig.datasourceConditions[datasourceCondition] = (Object as any).assign({},                     
                        globalFormConfig.datasourceConditions[datasourceCondition],
                        formConfig.datasourceConditions[datasourceCondition]);    
                } else {
                    formConfig.datasourceConditions[datasourceCondition] = 
                        globalFormConfig.datasourceConditions[datasourceCondition];
                }
            }
        }

        return formConfig;
    }
}
